import { Box, Typography, useTheme, Button, InputBase } from "@mui/material";
import { useSelector }  from "react-redux";
import { useState }     from "react";

import StraightenIcon    from "@mui/icons-material/Straighten";
import SearchIcon        from "@mui/icons-material/Search";
import AddIcon           from "@mui/icons-material/Add";
import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog   from "components/fixedSizeDialog";
import ResponsiveDialog  from "components/responsiveDialog";
import ItemUnitForm      from "../forms/itemUnitForm";
import { useItemUnitList }  from "api/hooks/inventory";
import { requester }        from "api/pesabackend";

export default function ItemUnits({ setIsSuccess }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { unitList, isLoadingItemUnitList, setNewItemUnitData, setSearch } = useItemUnitList(1, business_branch_id);

  const [open,        setOpen]        = useState(false);
  const [openEdit,    setOpenEdit]    = useState(false);
  const [openDelete,  setOpenDelete]  = useState(false);
  const [editData,    setEditData]    = useState({ name: "" });
  const [clickedItem, setClickedItem] = useState("");
  const [search,      setSearchLocal] = useState("");

  const handleClickEditOpen = async (id) => {
    setClickedItem(id);
    const res = await requester.get(`/inventory/business_product_units/get/${id}`);
    setEditData(res.data.data); setOpenEdit(true);
  };

  const handleClickDeleteOpen = async (id) => {
    setClickedItem(id);
    await requester.get(`/inventory/business_product_units/get/${id}`);
    setOpenDelete(true);
  };

  const handleClickDelete = async () => {
    await requester.post(`/inventory/business_product_units/delete/${clickedItem}`);
    setIsSuccess(true); setNewItemUnitData(true); setOpenDelete(false);
  };

  const columns = [
    { label: "#",         width: 40 },
    { label: "Unit Name"            },
    { label: "Actions",  align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <StraightenIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>Item Units</Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>{unitList.length} units</Typography>
          </Box>
        </Box>
        <Button onClick={() => setOpen(true)} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Add Unit
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: "0.75rem", height: "38px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px", maxWidth: "300px" }}>
        <SearchIcon sx={{ fontSize: "16px", color: textSec }} />
        <InputBase placeholder="Search unit name…" value={search} onChange={(e) => setSearchLocal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setSearch(search); }} sx={{ flex: 1, fontSize: "13px", color: textPri }} />
        <Box onClick={() => setSearch(search)} sx={{ fontSize: "11px", fontWeight: 500, color: accent, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>Search</Box>
      </Box>

      <ReportTable columns={columns} rows={unitList} loading={isLoadingItemUnitList}
        emptyMessage="No units found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.name}</Typography></RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                <Box onClick={() => handleClickEditOpen(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(186,117,23,0.3)", cursor: "pointer", color: "#BA7517", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(186,117,23,0.08)" } }}>
                  <EditOutlinedIcon sx={{ fontSize: "14px" }} /> Edit
                </Box>
                <Box onClick={() => handleClickDeleteOpen(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(226,75,74,0.3)", cursor: "pointer", color: "#e24b4a", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: "14px" }} /> Delete
                </Box>
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <ResponsiveDialog open={open} handleClickOpen={() => setOpen(true)} handleClose={() => setOpen(false)} dialogHeading="Add a New Item Unit" negativeResponse="Cancel"
        content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><ItemUnitForm formType="create" setOpen={setOpen} setNewItemUnitData={setNewItemUnitData} setIsSuccess={setIsSuccess} editData={editData} /></Box>}
      />
      <FixedSizeDialog open={openEdit} handleClose={() => setOpenEdit(false)} dialogHeading="Edit Item Unit" negativeResponse="Cancel" maxWidth="md"
        content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><ItemUnitForm formType="edit" id={clickedItem} editData={editData} setOpen={setOpenEdit} setNewItemUnitData={setNewItemUnitData} setIsSuccess={setIsSuccess} /></Box>}
      />
      <FixedSizeDialog open={openDelete} handleClose={() => setOpenDelete(false)} dialogHeading="Delete this unit?" negativeResponse="Cancel"
        positiveResponse="Yes, delete" handleSubmit={handleClickDelete} maxWidth="sm"
        content={
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px", p: "0.875rem 1rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>This will permanently delete the unit and cannot be undone.</Typography>
          </Box>
        }
      />
    </Box>
  );
}